import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  Rating
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Visibility,
  FilterList,
  Clear,
  CheckCircle,
  Cancel,
  Assignment,
  Description,
  Business,
  Person,
  DateRange,
  FileCopy,
  Print,
  Download,
  Upload,
  Work,
  Grade,
  Verified,
  Pending,
  Timeline
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';

const Stages = () => {
  const { user, isAdmin, isEnseignant, isEtudiant, isChefFiliere } = usePermissions();
  // Gestion des stages : direction et chef de filiere (ajout / edition /
  // suppression). Les enseignants gardent leurs acces historiques.
  const peutGererStages = isAdmin || isEnseignant || isChefFiliere;
  // Convention : signature par l'ecole / l'entreprise (direction + chef de filiere).
  const peutSignerConvention = isAdmin || isChefFiliere;
  
  const [stages, setStages] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState(null);
  
  // États pour les modals
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedStage, setSelectedStage] = useState(null);
  const [formData, setFormData] = useState({
    intitule: '',
    description: '',
    entreprise: {
      nom: '',
      adresse: '',
      ville: '',
      telephone: '',
      email: '',
      responsable: {
        nom: '',
        prenom: '',
        fonction: '',
        email: '',
        telephone: ''
      }
    },
    dateDebut: '',
    dateFin: '',
    statut: 'EN_ATTENTE'
  });

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [evaluationData, setEvaluationData] = useState({
    note: 0,
    appreciation: '',
    commentaires: ''
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      // Charger les stages
      let stagesUrl = '/api/stages';
      
      // Si étudiant, charger ses stages
      if (isEtudiant && user?.etudiant_id) {
        stagesUrl = `/api/stages/etudiant/${user.etudiant_id}`;
      } else if (isEnseignant && user?.enseignant_id) {
        stagesUrl = `/api/stages/encadrant/${user.enseignant_id}`;
      }
      
      const [stagesRes, statsRes] = await Promise.all([
        axios.get(stagesUrl, { headers }),
        axios.get('/api/stages/stats/global', { headers }).catch(() => ({ data: {} }))
      ]);
      
      setStages(stagesRes.data || []);
      setStats(statsRes.data);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const loadDemandes = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get('/api/stages/demandes', { headers });
      setDemandes(res.data || []);
    } catch (err) {
      console.error('Erreur chargement demandes:', err);
    }
  };

  const filteredStages = stages.filter(stage => {
    const search = searchTerm.toLowerCase();
    return (stage.intitule || '').toLowerCase().includes(search) ||
           (stage.etudiant_nom || '').toLowerCase().includes(search) ||
           (stage.etudiant_prenom || '').toLowerCase().includes(search) ||
           (stage.entreprise?.nom || '').toLowerCase().includes(search) ||
           (stage.numero || '').toLowerCase().includes(search);
  });

  const getStatutChip = (statut) => {
    const configs = {
      'EN_ATTENTE': { label: '⏳ En attente', color: '#f59e0b', bg: '#fef3c7' },
      'APPROUVE': { label: '✅ Approuvé', color: '#10b981', bg: '#dcfce7' },
      'EN_COURS': { label: '🔄 En cours', color: '#3b82f6', bg: '#dbeafe' },
      'TERMINE': { label: '🏁 Terminé', color: '#6b7280', bg: '#f3f4f6' },
      'VALIDE': { label: '🎓 Validé', color: '#8b5cf6', bg: '#f3e8ff' },
      'REJETE': { label: '❌ Rejeté', color: '#ef4444', bg: '#fee2e2' }
    };
    return configs[statut] || configs['EN_ATTENTE'];
  };

  const getMentionChip = (mention) => {
    const configs = {
      'PASSABLE': { label: 'Passable', color: '#f59e0b' },
      'ASSEZ_BIEN': { label: 'Assez Bien', color: '#3b82f6' },
      'BIEN': { label: 'Bien', color: '#10b981' },
      'TRES_BIEN': { label: 'Très Bien', color: '#8b5cf6' },
      'EXCELLENT': { label: 'Excellent', color: '#ef4444' }
    };
    return configs[mention] || null;
  };

  const handleOpenModal = (mode, stage = null) => {
    setModalMode(mode);
    if (stage) {
      setSelectedStage(stage);
      setFormData({
        intitule: stage.intitule || '',
        description: stage.description || '',
        entreprise: stage.entreprise || { nom: '', adresse: '', ville: '', telephone: '', email: '', responsable: { nom: '', prenom: '', fonction: '', email: '', telephone: '' } },
        dateDebut: stage.dateDebut?.split('T')[0] || '',
        dateFin: stage.dateFin?.split('T')[0] || '',
        statut: stage.statut || 'EN_ATTENTE'
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedStage(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      intitule: '',
      description: '',
      entreprise: {
        nom: '',
        adresse: '',
        ville: '',
        telephone: '',
        email: '',
        responsable: {
          nom: '',
          prenom: '',
          fonction: '',
          email: '',
          telephone: ''
        }
      },
      dateDebut: '',
      dateFin: '',
      statut: 'EN_ATTENTE'
    });
  };

  const handleSubmit = async () => {
    try {
      const headers = getAuthHeaders();
      if (modalMode === 'add') {
        await axios.post('/api/stages', formData, { headers });
        setSuccessMessage('Stage créé avec succès');
      } else {
        await axios.put(`/api/stages/${selectedStage._id}`, formData, { headers });
        setSuccessMessage('Stage modifié avec succès');
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'opération');
    }
  };

  const handleDelete = async (stage) => {
    if (!window.confirm(`Supprimer définitivement le stage "${stage.intitule}" ?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`/api/stages/${stage._id}`, { headers });
      setSuccessMessage('Stage supprimé avec succès');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleViewStage = (stage) => {
    setSelectedStage(stage);
    setViewModalOpen(true);
  };

  const handleSignatureEtudiant = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(
        `/api/stages/${selectedStage._id}/convention/signature-etudiant`,
        {},
        { headers }
      );
      setSuccessMessage('Convention signée par l\'étudiant');
      await loadData();
      setViewModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la signature');
    }
  };

  const handleSignatureEntreprise = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(
        `/api/stages/${selectedStage._id}/convention/signature-entreprise`,
        {},
        { headers }
      );
      setSuccessMessage('Convention signée par l\'entreprise');
      await loadData();
      setViewModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la signature');
    }
  };

  const handleSignatureEcole = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(
        `/api/stages/${selectedStage._id}/convention/signature-ecole`,
        {},
        { headers }
      );
      setSuccessMessage('Convention signée par l\'école');
      await loadData();
      setViewModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la signature');
    }
  };

  const handleSoumettreRapport = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `/api/stages/${selectedStage._id}/rapport`,
        {
          fichier: 'rapport.pdf',
          resume: 'Résumé du rapport de stage',
          motsCles: ['stage', 'informatique', 'développement']
        },
        { headers }
      );
      setSuccessMessage('Rapport soumis avec succès');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la soumission');
    }
  };

  const handleEvaluation = async () => {
    try {
      const headers = getAuthHeaders();
      const endpoint = isEnseignant ? 'encadrant' : 'tuteur';
      await axios.put(
        `/api/stages/${selectedStage._id}/evaluation/${endpoint}`,
        evaluationData,
        { headers }
      );
      setSuccessMessage('Évaluation enregistrée avec succès');
      setEvaluationModalOpen(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'évaluation');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            📋 Gestion des Stages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredStages.length} stage(s) trouvé(s)
            {stats && ` - ${stats.total || 0} au total`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          {peutGererStages && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal('add')}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              Nouveau Stage
            </Button>
          )}
          {isEtudiant && (
            <Button
              variant="contained"
              startIcon={<Assignment />}
              onClick={() => {/* Ouvrir modal demande de stage */}}
              sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
            >
              Demander un stage
            </Button>
          )}
        </Stack>
      </Box>

      {/* Statistiques */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Total Stages</Typography>
                <Typography variant="h4" fontWeight="bold">{stats.total || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Moyenne</Typography>
                <Typography variant="h4" fontWeight="bold">{stats.moyenne || 0}/20</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>En cours</Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stages.filter(s => s.statut === 'EN_COURS').length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Validés</Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stages.filter(s => s.statut === 'VALIDE').length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Onglets */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ px: 2 }}>
          <Tab label="📋 Tous les stages" />
          <Tab label="⏳ En attente" />
          <Tab label="🔄 En cours" />
          <Tab label="✅ Validés" />
        </Tabs>
      </Paper>

      {/* Recherche */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par intitulé, étudiant, entreprise ou numéro..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>N°</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Intitulé</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Étudiant</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Entreprise</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Période</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Note</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStages
              .filter(stage => {
                if (tabValue === 1) return stage.statut === 'EN_ATTENTE';
                if (tabValue === 2) return stage.statut === 'EN_COURS';
                if (tabValue === 3) return stage.statut === 'VALIDE';
                return true;
              })
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((stage) => {
                const statutStyle = getStatutChip(stage.statut);
                const mention = getMentionChip(stage.mention);
                return (
                  <TableRow key={stage._id} hover>
                    <TableCell sx={{ fontWeight: 600, color: '#667eea', fontFamily: 'monospace' }}>
                      {stage.numero}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{stage.intitule}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#667eea' }}>
                          {(stage.etudiant_prenom || '?')[0]?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{stage.etudiant_nom} {stage.etudiant_prenom}</Typography>
                          <Typography variant="caption" color="text.secondary">{stage.etudiant_cne}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Business fontSize="small" color="action" />
                        <Typography variant="body2">{stage.entreprise?.nom || '-'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {stage.dateDebut ? new Date(stage.dateDebut).toLocaleDateString('fr-FR') : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        → {stage.dateFin ? new Date(stage.dateFin).toLocaleDateString('fr-FR') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {stage.noteFinale ? (
                        <Box>
                          <Typography variant="h6" fontWeight="bold" color="#667eea">
                            {stage.noteFinale}/20
                          </Typography>
                          {mention && (
                            <Chip label={mention.label} size="small" sx={{ bgcolor: mention.color, color: 'white' }} />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Non évalué</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statutStyle.label}
                        size="small"
                        sx={{ bgcolor: statutStyle.bg, color: statutStyle.color }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Voir">
                          <IconButton size="small" color="info" onClick={() => handleViewStage(stage)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {peutGererStages && (
                          <>
                            <Tooltip title="Modifier">
                              <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', stage)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              <IconButton size="small" color="error" onClick={() => handleDelete(stage)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {isEnseignant && stage.statut === 'EN_COURS' && (
                          <Tooltip title="Évaluer">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => {
                                setSelectedStage(stage);
                                setEvaluationModalOpen(true);
                              }}
                            >
                              <Grade fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isEtudiant && stage.statut === 'EN_COURS' && !stage.rapport?.soumis && (
                          <Tooltip title="Soumettre rapport">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleSoumettreRapport()}
                            >
                              <Upload fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            {filteredStages.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucun stage trouvé</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredStages.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      {/* Modal Ajout/Modification */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? '➕ Ajouter un stage' : '✏️ Modifier le stage'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Intitulé *"
                  value={formData.intitule}
                  onChange={(e) => setFormData({ ...formData, intitule: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>🏢 Entreprise</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nom de l'entreprise *"
                  value={formData.entreprise.nom}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    entreprise: { ...formData.entreprise, nom: e.target.value }
                  })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Adresse"
                  value={formData.entreprise.adresse}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    entreprise: { ...formData.entreprise, adresse: e.target.value }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ville"
                  value={formData.entreprise.ville}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    entreprise: { ...formData.entreprise, ville: e.target.value }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  value={formData.entreprise.telephone}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    entreprise: { ...formData.entreprise, telephone: e.target.value }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.entreprise.email}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    entreprise: { ...formData.entreprise, email: e.target.value }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>👤 Responsable</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom"
                  value={formData.entreprise.responsable.nom}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    entreprise: { 
                      ...formData.entreprise, 
                      responsable: { ...formData.entreprise.responsable, nom: e.target.value }
                    }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prénom"
                  value={formData.entreprise.responsable.prenom}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    entreprise: { 
                      ...formData.entreprise, 
                      responsable: { ...formData.entreprise.responsable, prenom: e.target.value }
                    }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fonction"
                  value={formData.entreprise.responsable.fonction}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    entreprise: { 
                      ...formData.entreprise, 
                      responsable: { ...formData.entreprise.responsable, fonction: e.target.value }
                    }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.entreprise.responsable.email}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    entreprise: { 
                      ...formData.entreprise, 
                      responsable: { ...formData.entreprise.responsable, email: e.target.value }
                    }
                  })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date Début *"
                  type="date"
                  value={formData.dateDebut}
                  onChange={(e) => setFormData({ ...formData, dateDebut: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date Fin *"
                  type="date"
                  value={formData.dateFin}
                  onChange={(e) => setFormData({ ...formData, dateFin: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.statut}
                    onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                    label="Statut"
                  >
                    <MenuItem value="EN_ATTENTE">En attente</MenuItem>
                    <MenuItem value="APPROUVE">Approuvé</MenuItem>
                    <MenuItem value="EN_COURS">En cours</MenuItem>
                    <MenuItem value="TERMINE">Terminé</MenuItem>
                    <MenuItem value="VALIDE">Validé</MenuItem>
                    <MenuItem value="REJETE">Rejeté</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#667eea' }}>
            {modalMode === 'add' ? 'Créer' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Visualisation */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Work color="primary" />
              Détails du stage
            </Box>
            <Chip
              label={getStatutChip(selectedStage?.statut)?.label}
              size="small"
              sx={{ bgcolor: getStatutChip(selectedStage?.statut)?.bg, color: getStatutChip(selectedStage?.statut)?.color }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedStage && (
            <Box>
              {/* En-tête */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>N° Stage:</strong> {selectedStage.numero}</Typography>
                  <Typography variant="body2"><strong>Intitulé:</strong> {selectedStage.intitule}</Typography>
                  <Typography variant="body2"><strong>Description:</strong> {selectedStage.description || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Date Début:</strong> {selectedStage.dateDebut ? new Date(selectedStage.dateDebut).toLocaleDateString('fr-FR') : '-'}</Typography>
                  <Typography variant="body2"><strong>Date Fin:</strong> {selectedStage.dateFin ? new Date(selectedStage.dateFin).toLocaleDateString('fr-FR') : '-'}</Typography>
                  <Typography variant="body2"><strong>Durée:</strong> {selectedStage.dureeJours || 0} jours</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Étudiant */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>👨‍🎓 Étudiant</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Nom:</strong> {selectedStage.etudiant_nom} {selectedStage.etudiant_prenom}</Typography>
                  <Typography variant="body2"><strong>CNE:</strong> {selectedStage.etudiant_cne || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Encadrant:</strong> {selectedStage.encadrant_nom || 'Non assigné'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Entreprise */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>🏢 Entreprise</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Nom:</strong> {selectedStage.entreprise?.nom || '-'}</Typography>
                  <Typography variant="body2"><strong>Adresse:</strong> {selectedStage.entreprise?.adresse || '-'}</Typography>
                  <Typography variant="body2"><strong>Ville:</strong> {selectedStage.entreprise?.ville || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Téléphone:</strong> {selectedStage.entreprise?.telephone || '-'}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {selectedStage.entreprise?.email || '-'}</Typography>
                </Grid>
              </Grid>

              {selectedStage.entreprise?.responsable && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>👤 Responsable</Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Nom:</strong> {selectedStage.entreprise.responsable.nom} {selectedStage.entreprise.responsable.prenom}</Typography>
                      <Typography variant="body2"><strong>Fonction:</strong> {selectedStage.entreprise.responsable.fonction || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Email:</strong> {selectedStage.entreprise.responsable.email || '-'}</Typography>
                      <Typography variant="body2"><strong>Téléphone:</strong> {selectedStage.entreprise.responsable.telephone || '-'}</Typography>
                    </Grid>
                  </Grid>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Convention */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>📄 Convention</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <Chip
                    label={selectedStage.convention?.signeeParEtudiant ? '✅ Étudiant' : '⏳ Étudiant'}
                    size="small"
                    sx={{ bgcolor: selectedStage.convention?.signeeParEtudiant ? '#dcfce7' : '#fef3c7' }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Chip
                    label={selectedStage.convention?.signeeParEntreprise ? '✅ Entreprise' : '⏳ Entreprise'}
                    size="small"
                    sx={{ bgcolor: selectedStage.convention?.signeeParEntreprise ? '#dcfce7' : '#fef3c7' }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Chip
                    label={selectedStage.convention?.signeeParEcole ? '✅ École' : '⏳ École'}
                    size="small"
                    sx={{ bgcolor: selectedStage.convention?.signeeParEcole ? '#dcfce7' : '#fef3c7' }}
                  />
                </Grid>
              </Grid>

              {/* Actions convention */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {isEtudiant && !selectedStage.convention?.signeeParEtudiant && (
                  <Button size="small" variant="outlined" onClick={handleSignatureEtudiant}>
                    Signer (Étudiant)
                  </Button>
                )}
                {peutSignerConvention && !selectedStage.convention?.signeeParEntreprise && (
                  <Button size="small" variant="outlined" onClick={handleSignatureEntreprise}>
                    Signer (Entreprise)
                  </Button>
                )}
                {peutSignerConvention && !selectedStage.convention?.signeeParEcole && (
                  <Button size="small" variant="contained" onClick={handleSignatureEcole} sx={{ bgcolor: '#667eea' }}>
                    Signer (École)
                  </Button>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Rapport */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>📄 Rapport</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Chip
                    label={selectedStage.rapport?.soumis ? '✅ Soumis' : '⏳ Non soumis'}
                    size="small"
                    sx={{ bgcolor: selectedStage.rapport?.soumis ? '#dcfce7' : '#fef3c7' }}
                  />
                  {selectedStage.rapport?.dateSoumission && (
                    <Typography variant="caption" display="block">
                      Soumis le: {new Date(selectedStage.rapport.dateSoumission).toLocaleDateString('fr-FR')}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Chip
                    label={selectedStage.rapport?.valide ? '✅ Validé' : '⏳ En attente'}
                    size="small"
                    sx={{ bgcolor: selectedStage.rapport?.valide ? '#dcfce7' : '#fef3c7' }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Évaluations */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>⭐ Évaluations</Typography>
              <Grid container spacing={2}>
                {selectedStage.evaluations?.tuteur && (
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="bold">Tuteur</Typography>
                        <Typography variant="h4" color="#667eea">{selectedStage.evaluations.tuteur.note}/20</Typography>
                        <Typography variant="caption">{selectedStage.evaluations.tuteur.appreciation}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {selectedStage.evaluations?.encadrant && (
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="bold">Encadrant</Typography>
                        <Typography variant="h4" color="#667eea">{selectedStage.evaluations.encadrant.note}/20</Typography>
                        <Typography variant="caption">{selectedStage.evaluations.encadrant.appreciation}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {selectedStage.evaluations?.soutenance && (
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="bold">Soutenance</Typography>
                        <Typography variant="h4" color="#667eea">{selectedStage.evaluations.soutenance.note}/20</Typography>
                        <Typography variant="caption">{selectedStage.evaluations.soutenance.appreciation}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
              {selectedStage.noteFinale && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                  <Typography variant="body2">
                    <strong>Note finale:</strong> {selectedStage.noteFinale}/20
                    {selectedStage.mention && (
                      <Chip 
                        label={selectedStage.mention} 
                        size="small" 
                        sx={{ ml: 1, bgcolor: '#dbeafe', color: '#1e40af' }} 
                      />
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewModalOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Évaluation */}
      <Dialog open={evaluationModalOpen} onClose={() => setEvaluationModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Grade color="primary" />
            Évaluer le stagiaire
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Stagiaire:</strong> {selectedStage?.etudiant_nom} {selectedStage?.etudiant_prenom}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Stage:</strong> {selectedStage?.intitule}
            </Typography>
            <TextField
              fullWidth
              label="Note /20 *"
              type="number"
              inputProps={{ min: 0, max: 20, step: 0.5 }}
              value={evaluationData.note}
              onChange={(e) => setEvaluationData({ ...evaluationData, note: parseFloat(e.target.value) || 0 })}
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Appréciation</InputLabel>
              <Select
                value={evaluationData.appreciation}
                onChange={(e) => setEvaluationData({ ...evaluationData, appreciation: e.target.value })}
                label="Appréciation"
              >
                <MenuItem value="Excellent">⭐ Excellent</MenuItem>
                <MenuItem value="Très bien">⭐ Très bien</MenuItem>
                <MenuItem value="Bien">⭐ Bien</MenuItem>
                <MenuItem value="Assez bien">⭐ Assez bien</MenuItem>
                <MenuItem value="Passable">⭐ Passable</MenuItem>
                <MenuItem value="Insuffisant">⭐ Insuffisant</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Commentaires"
              value={evaluationData.commentaires}
              onChange={(e) => setEvaluationData({ ...evaluationData, commentaires: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvaluationModalOpen(false)}>Annuler</Button>
          <Button onClick={handleEvaluation} variant="contained" sx={{ bgcolor: '#667eea' }}>
            Enregistrer l'évaluation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Stages;


